import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldX, Camera, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function Verify() {
  const [status, setStatus] = useState(undefined);
  const [uploading, setUploading] = useState(false);
  const [reason, setReason] = useState("");
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/verification/status");
      setStatus(data.status);
    } catch {
      setStatus("unverified");
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setReason("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/verification/selfie", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus(data.status);
      setReason(data.reason || "");
      if (data.verified) toast.success("You're verified 🤍");
      else toast.error("Verification failed — try a clearer selfie");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const badge = {
    verified: { icon: ShieldCheck, cls: "text-green-400", label: "Verified", desc: "Your profile carries a verified badge." },
    rejected: { icon: ShieldX, cls: "text-red-400", label: "Not verified", desc: "We couldn't confirm a clear face. Please try again." },
    pending: { icon: ShieldAlert, cls: "text-yellow-400", label: "Pending", desc: "Verification in progress." },
    unverified: { icon: ShieldAlert, cls: "text-reveal-cream/50", label: "Not verified yet", desc: "Verify your identity to build trust." },
  }[status || "unverified"];

  const Icon = badge.icon;

  return (
    <div data-testid="verify-page" className="max-w-md">
      <div className="bg-reveal-burgundy/60 border border-reveal-rose/15 rounded-2xl p-8 text-center">
        <div className={`w-16 h-16 rounded-full bg-black/40 flex items-center justify-center mx-auto mb-5 ${badge.cls}`}>
          <Icon className="w-8 h-8" />
        </div>
        <h2 className="font-heading text-2xl text-reveal-cream mb-1" data-testid="verify-status">{badge.label}</h2>
        <p className="text-sm text-reveal-cream/60 mb-6">{badge.desc}</p>
        {reason && <p className="text-xs text-reveal-cream/40 mb-4">{reason}</p>}

        {status !== "verified" && (
          <>
            <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="verify-upload-button"
              className="inline-flex items-center gap-2 bg-reveal-rose text-black font-medium px-6 py-3 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-60">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {uploading ? "Verifying..." : "Upload a selfie"}
            </button>
            <input ref={inputRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="verify-file-input" />
            <p className="text-xs text-reveal-cream/40 mt-4">Face & age verification on every profile. Your selfie stays private.</p>
          </>
        )}
      </div>
    </div>
  );
}
